import  { useEffect, useState, useCallback } from 'react';
import {
  Trophy,
  Medal,
  Crown,
  Search,
  Award,
  Target,
  Zap,
  Star,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  GraduationCap
} from 'lucide-react';
import { Leaderboardservice, UserLeaderboardData, LeaderboardResponse} from '@/services/leaderboard.service';
import Pagination from '@/components/ui/Pagination';
import { useDebounce } from '@/utils/debounce';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { LEADERBOARD_PERMISSION } from '@/constants/constant-permissions';

export default function Leaderboard() {
  usePermissionRouteAccess([
    LEADERBOARD_PERMISSION.ADMIN_LEADERBOARD_READ,
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserLeaderboardData | null>(null);
  const [allLeaderboardData, setAllLeaderboardData] = useState<LeaderboardResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debounce search term (uses default delay from debounce.ts)
  const debouncedSearch = useDebounce(searchTerm);

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return '';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Profile Image Component
  const ProfileImage = ({ src, alt, size = 'md', borderColor }: { src?: string; alt: string; size?: 'sm' | 'md'; borderColor?: string }) => {
    const sizeClasses = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
    const textSizes = size === 'sm' ? 'text-xs' : 'text-sm';
    const hasImage = src && src.trim() !== '';
    const initials = getInitials(alt);

    if (hasImage) {
      return (
        <img
          src={src}
          alt={alt}
          className={`${sizeClasses} rounded-full object-cover ${borderColor ? `border-2 ${borderColor}` : ''}`}
        />
      );
    }

    return (
      <div className={`${sizeClasses} rounded-full ${borderColor ? `border-2 ${borderColor}` : ''} bg-gray-200 flex items-center justify-center`}>
        <span className={`${textSizes} font-semibold text-gray-600`}>{initials}</span>
      </div>
    );
  };
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Trophy className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-gray-600">#{rank}</span>;
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank <= 3) {
      return `bg-gradient-to-r ${
        rank === 1 ? 'from-yellow-400 to-yellow-600' :
        rank === 2 ? 'from-gray-300 to-gray-500' :
        'from-amber-400 to-amber-600'
      } text-white`;
    }
    return 'bg-gray-100 text-gray-700';
  };

  // Reset to first page when search changes
  useEffect(() => {
    if (debouncedSearch !== searchTerm && searchTerm !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearch, searchTerm]);

  const loadLeaderboardData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const promises: Promise<UserLeaderboardData | LeaderboardResponse>[] = [
        Leaderboardservice.getAllLeaderBoard({
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch || undefined
        })
      ];

      // Only fetch user data on initial load
      if (isInitialLoad) {
        promises.unshift(Leaderboardservice.getUserLeaderBoard());
      }

      const results = await Promise.allSettled(promises);

      if (isInitialLoad) {
        // Handle user data result
        if (results[0].status === 'fulfilled') {
          setCurrentUser(results[0].value as UserLeaderboardData);
        } else {
          console.error('Failed to fetch user data:', results[0].reason);
          setCurrentUser(null);
        }

        // Handle leaderboard data result
        if (results[1]?.status === 'fulfilled') {
          setAllLeaderboardData(results[1].value as LeaderboardResponse);
        } else if (results[1]?.status === 'rejected') {
          console.error('Failed to fetch leaderboard:', results[1].reason);
          setAllLeaderboardData(null);
        }
      } else {
        // Handle leaderboard data result for subsequent loads
        if (results[0].status === 'fulfilled') {
          setAllLeaderboardData(results[0].value as LeaderboardResponse);
        } else {
          console.error('Failed to fetch leaderboard:', results[0].reason);
        }
      }
    } catch (err: any) {
      console.error('Error loading leaderboard:', err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch]);

  // Initial load
  useEffect(() => {
    loadLeaderboardData(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subsequent loads (pagination, search)
  useEffect(() => {
    if (!loading) {
      loadLeaderboardData(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use top_performers from API response
  const topThree = allLeaderboardData?.top_performers || [];

  // Calculate total pages based on result_count when searching, otherwise use total_users
  const totalCount = debouncedSearch
    ? (allLeaderboardData?.result_count || 0)
    : (allLeaderboardData?.total_users || 0);
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <SkeletonLoader type="custom" width="40px" height="40px" className="rounded-lg bg-white/20" />
            <div className="flex-1">
              <SkeletonLoader type="title" className="h-8 w-48 mb-2 bg-white/30" />
              <SkeletonLoader type="text" className="h-4 w-96 bg-white/20" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <SkeletonLoader type="text" className="h-4 w-24 mb-2 bg-white/20" />
                <SkeletonLoader type="title" className="h-8 w-16 mb-1 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 w-20 bg-white/20" />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Performers Skeleton */}
          <div className="lg:col-span-2">
            <div className="card mb-6">
              <SkeletonLoader type="title" className="mb-4 w-40" />
              <div className="flex items-end justify-center gap-4 mb-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="text-center">
                    <SkeletonLoader type="custom" width="64px" height={i === 2 ? "96px" : i === 1 ? "80px" : "64px"} className="rounded-t-lg mb-3" />
                    <SkeletonLoader type="avatar" className="mx-auto mb-2" />
                    <SkeletonLoader type="text" className="h-4 w-24 mx-auto mb-1" />
                    <SkeletonLoader type="text" className="h-3 w-16 mx-auto" />
                  </div>
                ))}
              </div>
            </div>

            {/* Leaderboard Table Skeleton */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <SkeletonLoader type="title" className="w-32" />
                <SkeletonLoader type="custom" width="256px" height="40px" className="rounded-lg" />
              </div>

              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50">
                    <SkeletonLoader type="custom" width="48px" height="48px" className="rounded-lg" />
                    <SkeletonLoader type="avatar" />
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader type="text" className="h-5 w-48" />
                      <SkeletonLoader type="text" className="h-4 w-64" />
                      <SkeletonLoader type="text" className="h-4 w-40" />
                    </div>
                    <div className="text-right">
                      <SkeletonLoader type="text" className="h-6 w-16 mb-1" />
                      <SkeletonLoader type="text" className="h-4 w-12" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Skeleton */}
          <div className="space-y-6">
            {/* Stats Card Skeleton */}
            <div className="card">
              <SkeletonLoader type="title" className="mb-4 w-32" />
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <SkeletonLoader type="text" className="h-4 w-24" />
                    <SkeletonLoader type="text" className="h-5 w-16" />
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <SkeletonLoader type="text" className="h-4 w-32 mb-2" />
                <SkeletonLoader type="custom" height="8px" className="rounded-full mb-1" />
                <div className="flex justify-between">
                  <SkeletonLoader type="text" className="h-3 w-20" />
                  <SkeletonLoader type="text" className="h-3 w-8" />
                </div>
              </div>
            </div>

            {/* Courses Card Skeleton */}
            <div className="card">
              <SkeletonLoader type="title" className="mb-4 w-28" />
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg">
                    <SkeletonLoader type="text" className="h-4 w-full mb-2" />
                    <div className="flex items-center justify-between mb-2">
                      <SkeletonLoader type="text" className="h-4 w-16" />
                      <SkeletonLoader type="text" className="h-3 w-12" />
                    </div>
                    <SkeletonLoader type="custom" height="6px" className="rounded-full mb-1" />
                    <SkeletonLoader type="text" className="h-3 w-24" />
                  </div>
                ))}
              </div>
            </div>

            {/* Tips Card Skeleton */}
            <div className="card">
              <SkeletonLoader type="title" className="mb-4 w-28" />
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-2">
                    <SkeletonLoader type="custom" width="16px" height="16px" className="rounded mt-0.5" />
                    <SkeletonLoader type="text" className="flex-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Leaderboard</h1>
            <p className="text-yellow-100">Compete with fellow learners and climb to the top</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">Your Rank</span>
            </div>
            <div className="text-2xl font-bold">{currentUser?.rank ? `#${currentUser.rank}` : 'N/A'}</div>
            <div className="text-sm text-yellow-200">Current standing</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5" />
              <span className="font-semibold">XP Points</span>
            </div>
            <div className="text-2xl font-bold">{currentUser ? (currentUser.total_xp ?? 0).toLocaleString() : 'N/A'}</div>
            <div className="text-sm text-yellow-200">Total earned</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5" />
              <span className="font-semibold">Courses</span>
            </div>
            <div className="text-2xl font-bold">{currentUser ? (currentUser.total_courses ?? 0) : 'N/A'}</div>
            {currentUser && currentUser.courses && currentUser.courses.length > 0 ? (
              <div className="flex items-center gap-2 text-xs text-yellow-200 mt-1">
                <span className="flex items-center gap-1">
                    <div className="text-sm text-yellow-200">Total course inititated or completed</div>
                </span>
              </div>
            ) : (
              <div className="text-sm text-yellow-200">Enrolled</div>
            )}
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-5 h-5" />
              <span className="font-semibold">Certifications</span>
            </div>
            <div className="text-2xl font-bold">{currentUser ? (currentUser.total_certifications ?? 0) : 'N/A'}</div>
            {currentUser && currentUser.certifications && currentUser.certifications.length > 0 ? (
              <div className="flex items-center gap-2 text-xs text-yellow-200 mt-1">
                <span className="flex items-center gap-1">
                  <div className="text-sm text-yellow-200">Total cert inititated or completed</div>
                </span> 
              </div>
            ) : (
              <div className="text-sm text-yellow-200">Attempted</div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {/* <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Leaderboard Rankings</h2>
            <span className="text-sm text-gray-500">({allLeaderboardData?.total_users || 0} learners)</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search learners..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Period:</span>
            {periods.map((period) => (
              <button
                key={period.value}
                onClick={() => setSelectedPeriod(period.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === period.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
          
          <div className="h-6 border-l border-gray-300 mx-2"></div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div> */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top 3 */}
        <div className="lg:col-span-2">
          <div className="card mb-6 overflow-hidden relative">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,193,7,.1) 35px, rgba(255,193,7,.1) 70px)'
              }}></div>
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-md">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Hall of Fame</h3>
                    <p className="text-xs text-gray-500">Top 3 Champions</p>
                  </div>
                </div>
                <div className="px-3 py-1 bg-yellow-100 rounded-full">
                  <Star className="w-4 h-4 text-yellow-600 inline mr-1" />
                  <span className="text-xs font-semibold text-yellow-700">Elite</span>
                </div>
              </div>

              {topThree.length === 0 || !topThree.some(user => (user?.total_xp ?? 0) > 0) ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Trophy className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No users have started their courses yet</p>
                  <p className="text-sm text-gray-400 mt-1">Be the first to begin your learning journey!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* 1st Place - Featured Card (styled based on actual rank) */}
                  {topThree[0] && topThree[0].total_xp > 0 && (
                    <div className="relative group">
                      <div className={`absolute inset-0 rounded-2xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity ${
                        topThree[0].rank === 1
                          ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500'
                          : topThree[0].rank === 2
                            ? 'bg-gradient-to-r from-gray-300 to-gray-400'
                            : 'bg-gradient-to-r from-amber-300 to-amber-500'
                      }`}></div>
                      <div className="relative bg-white rounded-2xl p-5 shadow-lg">
                        <div className={`absolute -top-3 -right-3 rounded-xl px-3 py-1.5 shadow-lg rotate-6 ${
                          topThree[0].rank === 1
                            ? 'bg-gradient-to-br from-yellow-400 to-yellow-600'
                            : topThree[0].rank === 2
                              ? 'bg-gradient-to-br from-gray-300 to-gray-500'
                              : 'bg-gradient-to-br from-amber-400 to-amber-600'
                        }`}>
                          <div className="flex items-center gap-1">
                            <Crown className="w-4 h-4 text-white" />
                            <span className="text-white font-bold text-sm">#{topThree[0]?.rank ?? 1}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`absolute -inset-1 rounded-full blur-md opacity-75 animate-pulse ${
                              topThree[0].rank === 1
                                ? 'bg-gradient-to-r from-yellow-400 to-orange-500'
                                : topThree[0].rank === 2
                                  ? 'bg-gradient-to-r from-gray-300 to-gray-400'
                                  : 'bg-gradient-to-r from-amber-300 to-amber-500'
                            }`}></div>
                            <div className="relative w-16 h-16 rounded-full border-4 border-white shadow-xl overflow-hidden">
                              <ProfileImage
                                src={topThree[0]?.image}
                                alt={topThree[0]?.username || 'User'}
                                size="md"
                              />
                            </div>
                            <div className={`absolute -bottom-1 -right-1 rounded-full p-1.5 border-2 border-white shadow-md ${
                              topThree[0].rank === 1
                                ? 'bg-yellow-500'
                                : topThree[0].rank === 2
                                  ? 'bg-gray-400'
                                  : 'bg-amber-500'
                            }`}>
                              <Crown className="w-3 h-3 text-white" />
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-gray-900 text-lg truncate" title={topThree[0]?.username || 'User'}>
                                {topThree[0]?.username || 'Unknown User'}
                              </h4>
                              {topThree[0].rank === 1 && (
                                <div className="flex items-center gap-0.5">
                                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Zap className={`w-4 h-4 ${topThree[0].rank === 1 ? 'text-yellow-600' : topThree[0].rank === 2 ? 'text-gray-500' : 'text-amber-600'}`} />
                                <span className={`font-bold ${topThree[0].rank === 1 ? 'text-yellow-700' : topThree[0].rank === 2 ? 'text-gray-700' : 'text-amber-700'}`}>{(topThree[0]?.total_xp ?? 0).toLocaleString()}</span>
                                <span className="text-gray-500">XP</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <Target className="w-4 h-4 text-gray-400" />
                                <span>Level {topThree[0]?.total_level ?? 1}</span>
                              </span>
                            </div>
                          </div>

                          <div className={`hidden sm:flex flex-col items-center gap-1 px-4 py-2 rounded-xl ${
                            topThree[0].rank === 1
                              ? 'bg-gradient-to-br from-yellow-50 to-orange-50'
                              : topThree[0].rank === 2
                                ? 'bg-gradient-to-br from-gray-50 to-gray-100'
                                : 'bg-gradient-to-br from-amber-50 to-orange-50'
                          }`}>
                            <Trophy className={`w-6 h-6 ${topThree[0].rank === 1 ? 'text-yellow-600' : topThree[0].rank === 2 ? 'text-gray-500' : 'text-amber-600'}`} />
                            <span className="text-xs font-semibold text-gray-700">Champion</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2nd and 3rd Place - Compact Cards (styled based on actual rank) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* 2nd Position - styled based on actual rank */}
                    {topThree[1] && (topThree[1]?.total_xp ?? 0) > 0 && (() => {
                      const userRank = topThree[1].rank ?? 2;
                      const isRank1 = userRank === 1;
                      const isRank2 = userRank === 2;
                      return (
                        <div className="relative group">
                          <div className={`absolute inset-0 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity ${
                            isRank1 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : isRank2 ? 'bg-gradient-to-r from-gray-300 to-gray-400' : 'bg-gradient-to-r from-amber-300 to-amber-500'
                          }`}></div>
                          <div className={`relative bg-white rounded-xl p-4 shadow-md border ${
                            isRank1 ? 'border-yellow-200' : isRank2 ? 'border-gray-100' : 'border-amber-100'
                          }`}>
                            <div className={`absolute -top-2 -right-2 rounded-lg px-2.5 py-1 shadow ${
                              isRank1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : isRank2 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 'bg-gradient-to-br from-amber-400 to-amber-600'
                            }`}>
                              <span className="text-white font-bold text-sm flex items-center gap-1">
                                {isRank1 ? <Crown className="w-3.5 h-3.5" /> : <Medal className="w-3.5 h-3.5" />}
                                #{userRank}
                              </span>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="relative flex-shrink-0">
                                {isRank1 && (
                                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur-md opacity-75 animate-pulse"></div>
                                )}
                                <div className={`relative w-12 h-12 rounded-full border-3 shadow-md overflow-hidden ${
                                  isRank1 ? 'border-yellow-400' : isRank2 ? 'border-gray-300' : 'border-amber-400'
                                }`}>
                                  <ProfileImage
                                    src={topThree[1]?.image}
                                    alt={topThree[1]?.username || 'User'}
                                    size="sm"
                                  />
                                </div>
                                {isRank1 && (
                                  <div className="absolute -bottom-1 -right-1 bg-yellow-500 rounded-full p-1 border-2 border-white shadow-md">
                                    <Crown className="w-2.5 h-2.5 text-white" />
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-sm truncate mb-1" title={topThree[1]?.username || 'User'}>
                                  {topThree[1]?.username || 'Unknown User'}
                                </h4>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="flex items-center gap-1 text-gray-600">
                                    <Zap className={`w-3 h-3 ${isRank1 ? 'text-yellow-500' : isRank2 ? 'text-gray-500' : 'text-amber-500'}`} />
                                    <span className="font-semibold">{(topThree[1]?.total_xp ?? 0).toLocaleString()}</span>
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-600">Lvl {topThree[1]?.total_level ?? 1}</span>
                                </div>
                              </div>

                              {isRank1 && (
                                <div className="hidden sm:flex flex-col items-center gap-0.5 px-2 py-1 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg">
                                  <Trophy className="w-4 h-4 text-yellow-600" />
                                  <span className="text-[10px] font-semibold text-gray-700">Champion</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 3rd Position - styled based on actual rank */}
                    {topThree[2] && (topThree[2]?.total_xp ?? 0) > 0 && (() => {
                      const userRank = topThree[2].rank ?? 3;
                      const isRank1 = userRank === 1;
                      const isRank2 = userRank === 2;
                      return (
                        <div className="relative group">
                          <div className={`absolute inset-0 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity ${
                            isRank1 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : isRank2 ? 'bg-gradient-to-r from-gray-300 to-gray-400' : 'bg-gradient-to-r from-amber-300 to-amber-500'
                          }`}></div>
                          <div className={`relative bg-white rounded-xl p-4 shadow-md border ${
                            isRank1 ? 'border-yellow-200' : isRank2 ? 'border-gray-100' : 'border-amber-100'
                          }`}>
                            <div className={`absolute -top-2 -right-2 rounded-lg px-2.5 py-1 shadow ${
                              isRank1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : isRank2 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 'bg-gradient-to-br from-amber-400 to-amber-600'
                            }`}>
                              <span className="text-white font-bold text-sm flex items-center gap-1">
                                {isRank1 ? <Crown className="w-3.5 h-3.5" /> : isRank2 ? <Medal className="w-3.5 h-3.5" /> : <Trophy className="w-3.5 h-3.5" />}
                                #{userRank}
                              </span>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="relative flex-shrink-0">
                                {isRank1 && (
                                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur-md opacity-75 animate-pulse"></div>
                                )}
                                <div className={`relative w-12 h-12 rounded-full border-3 shadow-md overflow-hidden ${
                                  isRank1 ? 'border-yellow-400' : isRank2 ? 'border-gray-300' : 'border-amber-400'
                                }`}>
                                  <ProfileImage
                                    src={topThree[2]?.image}
                                    alt={topThree[2]?.username || 'User'}
                                    size="sm"
                                  />
                                </div>
                                {isRank1 && (
                                  <div className="absolute -bottom-1 -right-1 bg-yellow-500 rounded-full p-1 border-2 border-white shadow-md">
                                    <Crown className="w-2.5 h-2.5 text-white" />
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-sm truncate mb-1" title={topThree[2]?.username || 'User'}>
                                  {topThree[2]?.username || 'Unknown User'}
                                </h4>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="flex items-center gap-1 text-gray-600">
                                    <Zap className={`w-3 h-3 ${isRank1 ? 'text-yellow-500' : isRank2 ? 'text-gray-500' : 'text-amber-500'}`} />
                                    <span className="font-semibold">{(topThree[2]?.total_xp ?? 0).toLocaleString()}</span>
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-gray-600">Lvl {topThree[2]?.total_level ?? 1}</span>
                                </div>
                              </div>

                              {isRank1 && (
                                <div className="hidden sm:flex flex-col items-center gap-0.5 px-2 py-1 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg">
                                  <Trophy className="w-4 h-4 text-yellow-600" />
                                  <span className="text-[10px] font-semibold text-gray-700">Champion</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Full Leaderboard */}
          <div className="card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">All Rankings</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {debouncedSearch ? `${totalCount} result${totalCount !== 1 ? 's' : ''} found` : `${totalCount} total learners`}
                </p>
              </div>
              <div className="relative w-full sm:w-80">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 placeholder-gray-400 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {tableLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : allLeaderboardData?.leaderboard && allLeaderboardData.leaderboard.length > 0 ? (
              <>
                <div className="space-y-2 mb-4">
                  {allLeaderboardData.leaderboard.map((user, index) => {
                    if (!user) return null;
                    const isCurrentUser = user.user_id === currentUser?.user_id;
                    return (
                      <div
                        key={user.user_id || `user-${index}`}
                        className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                          isCurrentUser
                            ? 'bg-primary-50 border border-primary-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getRankBadge(user.rank ?? 0)}`}>
                          {getRankIcon(user.rank ?? 0)}
                        </div>

                        <ProfileImage
                          src={user.image}
                          alt={user.username || 'User'}
                          size="sm"
                        />

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-semibold ${isCurrentUser ? 'text-primary-700' : 'text-gray-900'}`}>
                              {user.username || 'Unknown User'}
                            </h4>
                            {isCurrentUser && (
                              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                                You
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>{user.email || 'No email'}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>Level {user.total_level ?? 1}</span>
                            <span>{user.courses?.length || 0} courses</span>
                            <span>{user.certifications?.length || 0} certifications</span>
                            <span>🔥 {user.streak ?? 0} day current </span>
                            <span>🔥 {user.longest_streak ?? 0} day longest </span>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="font-bold text-gray-900">{(user.total_xp ?? 0).toLocaleString()}</div>
                          <div className="text-sm text-gray-600">XP</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {allLeaderboardData && totalCount > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalCount}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 30, 50]}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Search className="w-12 h-12 mb-2 text-gray-400" />
                <p className="text-lg font-medium">No learners found</p>
                <p className="text-sm">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Your Stats */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Your Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Rank</span>
                <span className="font-bold text-gray-900">{currentUser?.rank ? `#${currentUser.rank}` : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total XP</span>
                <span className="font-bold text-green-600">{currentUser ? (currentUser.total_xp ?? 0).toLocaleString() : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Level</span>
                <span className="font-bold text-purple-600">{currentUser ? (currentUser.total_level ?? 0) : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Courses</span>
                <span className="font-bold text-blue-600">{currentUser ? (currentUser.total_courses ?? 0) : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Certifications</span>
                <span className="font-bold text-indigo-600">{currentUser ? (currentUser.total_certifications ?? 0) : 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Longest Streak</span>
                <span className="font-bold text-orange-600">
                  {currentUser ? `🔥 ${currentUser.longest_streak ?? 0} ${(currentUser.longest_streak ?? 0) > 1 ? 'days' : 'day'}` : 'N/A'}
                </span>
              </div>
               <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Streak</span>
                <span className="font-bold text-orange-600">
                  {currentUser ? `🔥 ${currentUser.streak ?? 0} ${(currentUser.streak ?? 0) > 1 ? 'days' : 'day'}` : 'N/A'}
                </span>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Average Progress</div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full"
                  style={{ width: `${currentUser?.average_level_progress || 0}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Level Progress</span>
                <span>{currentUser ? `${currentUser.average_level_progress ?? 0}%` : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Course Progress */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Your Courses</h3>
              {currentUser?.courses && currentUser.courses.length > 0 && (
                <span className="text-xs text-gray-500">{currentUser.total_courses ?? currentUser.courses.length} total</span>
              )}
            </div>
            {!currentUser?.courses || currentUser.courses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Award className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm font-medium">No courses found</p>
                <p className="text-xs text-gray-400 mt-1">Start a course to track your progress</p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentUser.courses
                  .slice(0, 3)
                  .map((course, index) => {
                    if (!course) return null;
                    const isCompleted = course.course_status === 'COMPLETED';
                    const isInProgress = course.course_status === 'IN_PROGRESS';
                    return (
                      <div key={course.course_id || `course-${index}`} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="font-medium text-gray-900 text-sm line-clamp-2" title={course.course_title ?? "N/A"}>
                            {course.course_title ?? "N/A"}
                          </span>
                          {isCompleted && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Completed
                            </span>
                          )}
                          {isInProgress && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              <Clock className="w-3 h-3" />
                              In Progress
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-700 text-xs">Level {course.level ?? 1}</span>
                          <span className="text-xs text-gray-600">{(course.xp ?? 0).toLocaleString()} XP</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-primary-600'}`}
                            style={{ width: `${course.levelProgress ?? 0}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {course.levelProgress ?? 0}% to next level
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Certifications Progress */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Your Certifications</h3>
              {currentUser?.certifications && currentUser.certifications.length > 0 && (
                <span className="text-xs text-gray-500">{currentUser.total_certifications ?? currentUser.certifications.length} total</span>
              )}
            </div>
            {!currentUser?.certifications || currentUser.certifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <GraduationCap className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm font-medium">No certifications found</p>
                <p className="text-xs text-gray-400 mt-1">Complete certification exams to earn badges</p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentUser.certifications
                  .slice(0, 3)
                  .map((cert, index) => {
                    if (!cert) return null;
                    return (
                      <div key={cert.certification_id || `cert-${index}`} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="font-medium text-gray-900 text-sm line-clamp-2" title={cert.certification_title ?? "N/A"}>
                            {cert.certification_title ?? "N/A"}
                          </span>
                          {cert.passed ? (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Passed
                            </span>
                          ) : (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Not Passed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-700 text-xs">Level {cert.level ?? 1}</span>
                          <span className="text-xs text-gray-600">{(cert.xp ?? 0).toLocaleString()} XP</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${cert.passed ? 'bg-green-500' : 'bg-orange-500'}`}
                            style={{ width: `${cert.passed ? 100 : Math.min(((cert.xp || 0) / (cert.nextLevelXP || 100)) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {cert.passed ? 'Certification earned!' : `${cert.xp ?? 0}/${cert.nextLevelXP ?? 100} XP to next level`}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Leaderboard Tips */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Climbing Tips</h3>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p>Complete courses consistently for streak bonuses</p>
              </div>
              <div className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p>Maintain daily learning streaks for extra XP</p>
              </div>
              <div className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p>Complete lessons to earn base and bonus XP</p>
              </div>
              <div className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p>Level up in multiple courses for higher ranks</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}